import _ from 'lodash';
import { convertDuration, splitDuration } from '@/datetime/dateTime.utilities';
import { InterpolationMethod, SAMPLE_FROM_SCALARS } from '@/services/calculationRunner.constants';
import { getDefaultMaxCapsuleDuration } from '@/services/systemConfiguration.utilities';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import type { FrontendDuration } from '@/services/systemConfiguration.types';
import type { BackendDuration } from '@/services/systemConfiguration.types';
import type { BaseToolPersistedState, ToolConfigParams, ToolRehydratePayload } from '@/toolSelection/baseTool.store';
import type { StoredStatistic } from '@/tools/StatisticSelector.molecule';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

interface StatParameters {
  stat: StoredStatistic | null;
  parameters: {
    timeUnits: string;
    percentile: number | null;
    multiplier?: number | null;
  };
}

interface SignalFromConditionEditPayload extends ToolRehydratePayload {
  formula?: string;
  signalMetadata?: {
    maxInterpolation?: FrontendDuration;
  };
}

interface SignalFromConditionState extends BaseToolPersistedState {
  inputItem: { predicate: [string, string] };
  condition: { predicate: [string, string] };
  interpolation: InterpolationMethod;
  stat: {
    key: string | undefined;
    timeUnits: string;
  };
  maximumDurationInputOverride: ValueWithUnitsItem | undefined;
  maximumDurationBoundingOverride: ValueWithUnitsItem | undefined;
  keyMethod: string | undefined;
  color: string;
  maxInterpolation: FrontendDuration | undefined;
}

export type SignalFromConditionDehydratedState = SignalFromConditionState;

export class SignalFromConditionStore extends BaseToolStore {
  static readonly storeName = 'sqSignalFromConditionStore';
  parameterDefinitions = {
    inputItem: { predicate: ['name', 'series'] },
    condition: { predicate: ['name', 'capsules'] },
  };
  type = TREND_TOOLS.SIGNAL_FROM_CONDITION;

  initialize(): void {
    const initialState: SignalFromConditionState = {
      ...BASE_TOOL_COMMON_PROPS,
      inputItem: { predicate: ['name', 'series'] },
      condition: { predicate: ['name', 'capsules'] },
      interpolation: InterpolationMethod.DISCRETE,
      stat: {
        key: undefined,
        timeUnits: 's',
      },
      maximumDurationInputOverride: undefined,
      maximumDurationBoundingOverride: undefined,
      keyMethod: undefined,
      color: '',
      maxInterpolation: undefined,
    };
    this.state = this.immutable(initialState);
  }

  get inputItem(): any {
    return this.state.get('inputItem');
  }

  get condition(): any {
    return this.state.get('condition');
  }

  get interpolation(): InterpolationMethod {
    return this.state.get('interpolation');
  }

  get stat(): StoredStatistic {
    return this.state.get('stat');
  }

  get maximumDurationInputOverride(): ValueWithUnitsItem {
    return this.state.get('maximumDurationInputOverride') || getDefaultMaxCapsuleDuration();
  }

  get maximumDurationBoundingOverride(): ValueWithUnitsItem {
    return this.state.get('maximumDurationBoundingOverride') || getDefaultMaxCapsuleDuration();
  }

  get keyMethod(): string {
    return this.state.get('keyMethod');
  }

  get color(): string {
    return this.state.get('color');
  }

  get maxInterpolation(): FrontendDuration | undefined {
    return this.state.get('maxInterpolation');
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @return {Object} State for the store
   */
  dehydrate(): SignalFromConditionDehydratedState {
    return this.state.serialize();
  }

  /**
   * Sets the state
   *
   * @param {Object} dehydratedState - Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: Partial<SignalFromConditionDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  getStatAndParameters(formula: string): StatParameters {
    const match = /\.aggregate\((\w*)\((?:'|")?(.*?)(?:'|")?\)/.exec(formula);
    if (!match) {
      return { stat: null, parameters: { timeUnits: 's', percentile: null } };
    }

    const [, operator, arg] = match;
    const method = _.find(SAMPLE_FROM_SCALARS.VALUE_METHODS, (valueMethod) => _.startsWith(valueMethod.stat, operator));
    if (!method) {
      return { stat: null, parameters: { timeUnits: 's', percentile: null } };
    }

    const parameters: StatParameters['parameters'] = {
      timeUnits: 's',
      percentile: null,
    };

    const storedStat: StoredStatistic = {
      key: method.key,
      timeUnits: 's',
      percentile: null,
      multiplier: null,
    };

    if (method.key === 'percentile') {
      const percentile = parseInt(arg, 10);
      const parsedPercentile = _.isNaN(percentile) ? null : percentile;
      parameters.percentile = parsedPercentile;
      storedStat.percentile = parsedPercentile;
    } else if (method.key === 'standardDeviationBoundary') {
      const multiplier = parseFloat(arg);
      const parsedMultiplier = _.isNaN(multiplier) ? null : multiplier;
      parameters.multiplier = parsedMultiplier;
      storedStat.multiplier = parsedMultiplier;
    } else {
      const timeUnits =
        _.chain([arg])
          .reject((text) => _.toLower(text) === 'true' || _.toLower(text) === 'false')
          .head()
          .value() || 's';
      parameters.timeUnits = timeUnits;
      storedStat.timeUnits = timeUnits;
    }

    return { stat: storedStat, parameters };
  }

  /**
   * Removes properties from config which are stored as part of the formula.
   * Signal from condition gets everything it needs from the formula so the config only contains the tool type and
   * whether the advanced section is expanded or not.
   *
   * @param {Object} config - The state that will be saved to UIConfig
   * @return {Object} The modified config
   */
  modifyConfigParams<TConfig extends ToolConfigParams>(config: TConfig): TConfig {
    const allowedKeys = new Set(['type', 'advancedParametersCollapsed']);
    Object.keys(config).forEach((key) => {
      if (!allowedKeys.has(key)) {
        delete (config as Record<string, unknown>)[key];
      }
    });
    return config;
  }

  protected readonly handlers: BaseToolStore['handlers'] & {
    SIGNAL_FROM_CONDITION_SET_INTERPOLATION: (payload: { interpolation: InterpolationMethod }) => void;
    SIGNAL_FROM_CONDITION_SET_STAT: (payload: { stat: StoredStatistic }) => void;
    SIGNAL_FROM_CONDITION_SET_INPUT_OVERRIDE: (payload: { inputOverride: ValueWithUnitsItem }) => void;
    SIGNAL_FROM_CONDITION_SET_BOUNDING_OVERRIDE: (payload: { boundingOverride: ValueWithUnitsItem }) => void;
    SIGNAL_FROM_CONDITION_SET_KEY_METHOD: (payload: { keyMethod: string }) => void;
    SIGNAL_FROM_CONDITION_SET_COLOR: (payload: { color: string }) => void;
    SIGNAL_FROM_CONDITION_SET_MAX_INTERPOLATION: (payload: { maxInterpolation?: FrontendDuration }) => void;
    TOOL_REHYDRATE_FOR_EDIT: (payload: SignalFromConditionEditPayload) => void;
  } = {
    ...this.baseHandlers,

    SIGNAL_FROM_CONDITION_SET_INTERPOLATION: (payload: { interpolation: InterpolationMethod }) => {
      this.state.set('interpolation', payload.interpolation);
    },

    SIGNAL_FROM_CONDITION_SET_STAT: (payload: { stat: StoredStatistic }) => {
      this.state.set('stat', payload.stat);
    },

    SIGNAL_FROM_CONDITION_SET_INPUT_OVERRIDE: (payload: { inputOverride: ValueWithUnitsItem }) => {
      this.state.set('maximumDurationInputOverride', payload.inputOverride);
    },

    SIGNAL_FROM_CONDITION_SET_BOUNDING_OVERRIDE: (payload: { boundingOverride: ValueWithUnitsItem }) => {
      this.state.set('maximumDurationBoundingOverride', payload.boundingOverride);
    },

    SIGNAL_FROM_CONDITION_SET_KEY_METHOD: (payload: { keyMethod: string }) => {
      this.state.set('keyMethod', payload.keyMethod);
    },

    SIGNAL_FROM_CONDITION_SET_COLOR: (payload: { color: string }) => {
      this.state.set('color', payload.color);
    },

    SIGNAL_FROM_CONDITION_SET_MAX_INTERPOLATION: (payload: { maxInterpolation?: FrontendDuration }) => {
      this.state.set('maxInterpolation', payload.maxInterpolation);
    },

    /**
     * Adds the formula and parameters to the config as part of what gets rehydrated when the tool is loaded.
     *
     * @param {Object} payload - An object with the necessary state to populate the edit form.
     * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
     * @param {Object[]} payload.parameters - The parameters used in the formula
     * @param {String} payload.selectedOperator - The selected operator from the UI Config
     */
    TOOL_REHYDRATE_FOR_EDIT: (payload: SignalFromConditionEditPayload) => {
      if (payload?.type === TREND_TOOLS.SIGNAL_FROM_CONDITION) {
        this.rehydrateForEdit(payload);

        const inputOverrideDuration = _.get(/\$series\.setMaximumDuration\((.*?)\)/.exec(payload.formula ?? ''), '1');
        const boundingOverrideDuration = _.get(
          /\$capsules\.setMaximumDuration\((.*?)\)/.exec(payload.formula ?? ''),
          '1',
        );
        const { stat, parameters } = this.getStatAndParameters(payload.formula ?? '');
        const keyMethod = _.get(/(\w*?)Key\(\)/.exec(payload.formula ?? ''), '1');

        let interpolation;
        const isStep = (payload.formula ?? '').indexOf('toStep()') > -1;
        if (isStep) {
          interpolation = InterpolationMethod.STEP;
        } else if (Number(_.get(payload, 'signalMetadata.maxInterpolation.value', 0)) > 0) {
          interpolation = InterpolationMethod.LINEAR;
        } else {
          interpolation = InterpolationMethod.DISCRETE;
        }

        const maxInterpolation = convertDuration(
          _.get(payload, 'signalMetadata.maxInterpolation') as BackendDuration | undefined,
        );
        this.state.set('signalMetadata', { maxInterpolation });

        this.state.set('interpolation', interpolation);
        this.state.set('stat', {
          key: stat?.key ?? null,
          timeUnits: stat?.timeUnits ?? parameters.timeUnits,
          percentile: stat?.percentile ?? parameters.percentile ?? null,
          multiplier: stat?.multiplier ?? parameters.multiplier ?? null,
        });
        this.state.set('keyMethod', keyMethod);
        const inputOverride = inputOverrideDuration ? splitDuration(inputOverrideDuration) : undefined;
        const boundingOverride = boundingOverrideDuration ? splitDuration(boundingOverrideDuration) : undefined;
        this.state.set('maximumDurationInputOverride', inputOverride ?? getDefaultMaxCapsuleDuration());
        this.state.set('maximumDurationBoundingOverride', boundingOverride ?? getDefaultMaxCapsuleDuration());
      }
    },
  };
}
